import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch, FormControlLabel,
  IconButton, Chip, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem,
  Divider
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';

const emptyForm = {
  name: '',
  code: '',
  description: '',
  is_free: false,
  is_active: true,
  show_on_signup: true,
  currency: 'USD',
  billing_period: 'monthly',
  base_price: 0,
  discount_percent: 0,
  discount_amount: 0,
  metrics: [],
  sort_order: 0
};

const computePreview = (form) => {
  if (form.is_free) return 0;
  const metricsTotal = (form.metrics || [])
    .filter((m) => m.enabled)
    .reduce((sum, m) => sum + (Number(m.price) || 0), 0);
  const subtotal = (Number(form.base_price) || 0) + metricsTotal;
  const afterPercent = subtotal * (1 - (Number(form.discount_percent) || 0) / 100);
  return Math.max(0, Number((afterPercent - (Number(form.discount_amount) || 0)).toFixed(2)));
};

const PricingSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [metricCatalog, setMetricCatalog] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const previewPrice = useMemo(() => computePreview(form), [form]);

  const load = async () => {
    setLoading(true);
    try {
      const [plansRes, metricsRes] = await Promise.all([
        api.get('/catalog/pricing/plans'),
        api.get('/catalog/pricing/metrics')
      ]);
      setPlans(plansRes.data?.data || []);
      setMetricCatalog(metricsRes.data?.data?.metrics || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load pricing');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const mergeWithCatalog = (planMetrics = []) => {
    const byKey = new Map((metricCatalog || []).map((m) => [m.key, { ...m }]));
    (planMetrics || []).forEach((metric) => {
      if (!byKey.has(metric.key)) return;
      const base = byKey.get(metric.key);
      byKey.set(metric.key, {
        ...base,
        enabled: metric.enabled !== undefined ? Boolean(metric.enabled) : base.enabled,
        value: metric.value !== undefined ? Number(metric.value) : base.value,
        price: metric.price !== undefined ? Number(metric.price) : base.price
      });
    });
    return Array.from(byKey.values());
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm,
      metrics: metricCatalog.map((m) => ({ ...m }))
    });
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      name: plan.name,
      code: plan.code,
      description: plan.description || '',
      is_free: plan.is_free,
      is_active: plan.is_active,
      show_on_signup: plan.show_on_signup,
      currency: plan.currency || 'USD',
      billing_period: plan.billing_period || 'monthly',
      base_price: Number(plan.base_price) || 0,
      discount_percent: Number(plan.discount_percent) || 0,
      discount_amount: Number(plan.discount_amount) || 0,
      metrics: mergeWithCatalog(plan.metrics),
      sort_order: plan.sort_order || 0
    });
    setDialogOpen(true);
  };

  const updateMetric = (index, patch) => {
    setForm((prev) => {
      const metrics = [...prev.metrics];
      metrics[index] = { ...metrics[index], ...patch };
      return { ...prev, metrics };
    });
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, final_price: previewPrice };
      if (editing) {
        await api.put(`/catalog/pricing/plans/${editing.id}`, payload);
        toast.success('Plan updated');
      } else {
        await api.post('/catalog/pricing/plans', payload);
        toast.success('Plan created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try {
      await api.delete(`/catalog/pricing/plans/${plan.id}`);
      toast.success('Plan deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pricing Plans</Typography>
          <Typography variant="body2" color="text.secondary">
            Build plans using metrics from Settings → Pricing Metrics (Standard + Custom).
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          {canEdit && <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Create Plan</Button>}
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Manage the metric catalog in{' '}
        <Button component={RouterLink} to="/settings/pricing-metrics" size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
          Settings → Pricing Metrics
        </Button>
        . New and edited plans automatically include all standard and custom metrics from that catalog.
      </Alert>

      {!metricCatalog.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No pricing metrics found. Add Standard/Custom metrics first, then create plans.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Plan</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Billing</TableCell>
              <TableCell>Final Price</TableCell>
              <TableCell>Signup</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell>
                  <Typography fontWeight={600}>{plan.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{plan.description}</Typography>
                </TableCell>
                <TableCell><code>{plan.code}</code></TableCell>
                <TableCell>{plan.billing_period}</TableCell>
                <TableCell>
                  {plan.is_free ? (
                    <Chip size="small" color="success" label="Free" />
                  ) : (
                    `${plan.currency} ${Number(plan.final_price).toFixed(2)}`
                  )}
                </TableCell>
                <TableCell>{plan.show_on_signup ? 'Visible' : 'Hidden'}</TableCell>
                <TableCell align="right">
                  {canEdit && (
                    <>
                      <IconButton size="small" onClick={() => openEdit(plan)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(plan)}><DeleteIcon fontSize="small" /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!plans.length && (
              <TableRow><TableCell colSpan={6} align="center">No pricing plans yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? `Edit Plan: ${editing.name}` : 'Create Pricing Plan'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Plan Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            {!editing && <TextField label="Code (optional)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} fullWidth />}
            <TextField label="Description" multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Billing Period</InputLabel>
                <Select label="Billing Period" value={form.billing_period} onChange={(e) => setForm({ ...form, billing_period: e.target.value })}>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="one_time">One-time</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} fullWidth />
            </Box>
            <FormControlLabel
              control={<Switch checked={form.is_free} onChange={(e) => setForm({ ...form, is_free: e.target.checked })} />}
              label="Free plan (final price = 0)"
            />
            <TextField
              type="number"
              label="Base Price"
              value={form.base_price}
              onChange={(e) => setForm({ ...form, base_price: e.target.value })}
              disabled={form.is_free}
              fullWidth
            />

            <Divider />
            <Typography variant="subtitle1" fontWeight={700}>Metrics from Catalog</Typography>
            <Typography variant="body2" color="text.secondary">
              Enable/disable metrics for this plan and set values/prices. Source shows Standard vs Custom.
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Enabled</TableCell>
                  <TableCell>Metric</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.metrics.map((metric, index) => (
                  <TableRow key={metric.key}>
                    <TableCell>
                      <Switch
                        checked={Boolean(metric.enabled)}
                        onChange={(e) => updateMetric(index, { enabled: e.target.checked })}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{metric.label}</Typography>
                      <Typography variant="caption" color="text.secondary"><code>{metric.key}</code></Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={metric.source === 'custom' ? 'Custom' : 'Standard'}
                        color={metric.source === 'custom' ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={metric.value}
                        onChange={(e) => updateMetric(index, { value: e.target.value })}
                        disabled={!metric.enabled}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={metric.price}
                        onChange={(e) => updateMetric(index, { price: e.target.value })}
                        disabled={!metric.enabled || form.is_free}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!form.metrics.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No metrics in catalog. Configure Pricing Metrics first.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="number"
                label="Discount %"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                disabled={form.is_free}
                fullWidth
              />
              <TextField
                type="number"
                label="Discount Amount"
                value={form.discount_amount}
                onChange={(e) => setForm({ ...form, discount_amount: e.target.value })}
                disabled={form.is_free}
                fullWidth
              />
            </Box>

            <Alert severity="success">
              Final plan price: <strong>{form.currency} {previewPrice.toFixed(2)}</strong>
              {form.is_free ? ' (Free)' : ''}
            </Alert>

            <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />} label="Active" />
            <FormControlLabel control={<Switch checked={form.show_on_signup} onChange={(e) => setForm({ ...form, show_on_signup: e.target.checked })} />} label="Show on Tenant Signup" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name}>Save Plan</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingSettings;
